export interface StorageExportItem {
  name: string;
  quantity: number;
  storeQuantity?: number;
  takenQuantity?: number;
  location?: string | null;
  supplier?: string | null;
}

export interface BuildStorageExcelTsvOptions {
  includeStoreQuantity?: boolean;
  includeTakenQuantity?: boolean;
}

function sanitizeCell(value: string): string {
  return value.replace(/\r?\n/g, ' ').replace(/\t/g, ' ');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function sortItems(items: StorageExportItem[]): StorageExportItem[] {
  return [...items].sort((a, b) =>
    a.name.localeCompare(b.name, 'lt', { sensitivity: 'base' })
  );
}

function getHeaders(options: BuildStorageExcelTsvOptions): string[] {
  if (options.includeStoreQuantity) {
    return ['Pavadinimas', 'Sandėlyje', 'Parduotuvėje', 'Vieta', 'Tiekėjas'];
  }
  if (options.includeTakenQuantity) {
    return ['Pavadinimas', 'Sandėlyje', 'Paimta', 'Vieta', 'Tiekėjas'];
  }
  return ['Pavadinimas', 'Sandėlyje', 'Vieta', 'Tiekėjas'];
}

export function buildStorageExcelTsv(
  items: StorageExportItem[],
  options: BuildStorageExcelTsvOptions
): string {
  const headers = getHeaders(options);

  const rows = sortItems(items).map((item) => {
    const name = sanitizeCell(item.name || '');
    const sandelyje = String(item.quantity ?? 0);
    const vieta = sanitizeCell(item.location?.trim() || '');
    const tiekejas = sanitizeCell(item.supplier?.trim() || '');

    if (options.includeStoreQuantity) {
      const parduotuveje = String(item.storeQuantity ?? 0);
      return [name, sandelyje, parduotuveje, vieta, tiekejas].join('\t');
    }
    if (options.includeTakenQuantity) {
      const paimta = String(item.takenQuantity ?? 0);
      return [name, sandelyje, paimta, vieta, tiekejas].join('\t');
    }
    return [name, sandelyje, vieta, tiekejas].join('\t');
  });

  return [headers.join('\t'), ...rows].join('\n');
}

export function buildStorageExcelHtml(
  items: StorageExportItem[],
  options: BuildStorageExcelTsvOptions
): string {
  const headers = getHeaders(options);
  const includeSecondary = options.includeStoreQuantity || options.includeTakenQuantity;

  const colgroup = includeSecondary
    ? `
      <col style="width: 320px" />
      <col style="width: 80px" />
      <col style="width: 90px" />
      <col style="width: 120px" />
      <col style="width: 220px" />`
    : `
      <col style="width: 320px" />
      <col style="width: 80px" />
      <col style="width: 120px" />
      <col style="width: 220px" />`;

  const headerStyle =
    'background-color: #7b1fa2; color: #ffffff; font-weight: bold; text-align: left; padding: 8px 10px; white-space: nowrap;';
  const textCellStyle =
    'padding: 6px 10px; text-align: left; vertical-align: top; white-space: normal; mso-number-format: "\\@";';
  const numCellStyle =
    'padding: 6px 10px; text-align: right; vertical-align: top; white-space: nowrap;';

  let html =
    '<html><head><meta charset="utf-8"></head><body>' +
    '<table border="1" cellpadding="0" cellspacing="0" style="border-collapse: collapse; font-family: Calibri, Arial, sans-serif; font-size: 11pt; table-layout: fixed;">' +
    `<colgroup>${colgroup}</colgroup>` +
    '<thead><tr>';

  for (const header of headers) {
    const align = header === 'Sandėlyje' || header === 'Parduotuvėje' || header === 'Paimta' ? 'text-align: right;' : '';
    html += `<th style="${headerStyle}${align}">${escapeHtml(header)}</th>`;
  }
  html += '</tr></thead><tbody>';

  for (const item of sortItems(items)) {
    const name = escapeHtml(sanitizeCell(item.name || ''));
    const sandelyje = String(item.quantity ?? 0);
    const vieta = escapeHtml(sanitizeCell(item.location?.trim() || ''));
    const tiekejas = escapeHtml(sanitizeCell(item.supplier?.trim() || ''));

    html += '<tr>';
    html += `<td style="${textCellStyle}">${name}</td>`;
    html += `<td style="${numCellStyle}">${sandelyje}</td>`;
    if (options.includeStoreQuantity) {
      html += `<td style="${numCellStyle}">${String(item.storeQuantity ?? 0)}</td>`;
    } else if (options.includeTakenQuantity) {
      html += `<td style="${numCellStyle}">${String(item.takenQuantity ?? 0)}</td>`;
    }
    html += `<td style="${textCellStyle}">${vieta}</td>`;
    html += `<td style="${textCellStyle}">${tiekejas}</td>`;
    html += '</tr>';
  }

  html += '</tbody></table></body></html>';
  return html;
}

export async function copyStorageItemsToExcel(
  items: StorageExportItem[],
  options: BuildStorageExcelTsvOptions
): Promise<void> {
  const tsv = buildStorageExcelTsv(items, options);
  const html = buildStorageExcelHtml(items, options);

  const htmlBlob = new Blob([html], { type: 'text/html' });
  const textBlob = new Blob([tsv], { type: 'text/plain' });

  await navigator.clipboard.write([
    new ClipboardItem({
      'text/html': htmlBlob,
      'text/plain': textBlob
    })
  ]);
}
